import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import {
  listStates,
  listCities,
  listNeighborhoods,
} from "../../services/pharmaApi";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(1),
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  form: {
    marginTop: theme.spacing(1),
  },
  cepField: {
    position: "relative",
  },
  cepSearchButton: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
  },
  labelField: {
    "& .MuiInputBase-input": {
      fontWeight: 600,
    },
  },
  autocomplete: {
    "& .MuiAutocomplete-inputRoot": {
      padding: "0 !important",
    },
    "& .MuiAutocomplete-input": {
      padding: "10.5px 14px !important",
    },
  },
}));

const AddressFormModal = ({
  open,
  onClose,
  onSave,
  address = null, // null = new address, object = edit
  customerId,
  isLoading = false,
}) => {
  const classes = useStyles();

  // Form state
  const [label, setLabel] = useState("Casa");
  const [zipcode, setZipcode] = useState("");
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [neighborhoodObj, setNeighborhoodObj] = useState(null);
  const [city, setCity] = useState("");
  const [cityObj, setCityObj] = useState(null);
  const [state, setState] = useState("");
  const [reference, setReference] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [searchingCep, setSearchingCep] = useState(false);

  // Dropdown data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  // Load states on mount
  useEffect(() => {
    if (open) {
      loadStates();
    }
  }, [open]);

  // Load cities when state changes
  useEffect(() => {
    if (state) {
      loadCities(state);
    } else {
      setCities([]);
      setCityObj(null);
      setCity("");
    }
  }, [state]);

  // Load neighborhoods when city changes
  useEffect(() => {
    if (cityObj?.id) {
      loadNeighborhoods(cityObj.id);
    } else {
      setNeighborhoods([]);
      setNeighborhoodObj(null);
      setNeighborhood("");
    }
  }, [cityObj]);

  const loadStates = async () => {
    setLoadingStates(true);
    try {
      const data = await listStates();
      setStates(data || []);
    } catch (error) {
      console.error("Error loading states:", error);
      // Fallback to empty array - user can still type manually
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const loadCities = async (stateCode) => {
    setLoadingCities(true);
    try {
      const data = await listCities({ state: stateCode, isActive: true, limit: 500 });
      setCities(data.data || []);
    } catch (error) {
      console.error("Error loading cities:", error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const loadNeighborhoods = async (cityId) => {
    setLoadingNeighborhoods(true);
    try {
      const data = await listNeighborhoods({ cityId, isActive: true, limit: 500 });
      setNeighborhoods(data.data || []);
    } catch (error) {
      console.error("Error loading neighborhoods:", error);
      setNeighborhoods([]);
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (address) {
      setLabel(address.label || "Casa");
      setZipcode(address.zipcode || "");
      setStreet(address.street || "");
      setNumber(address.number || "");
      setComplement(address.complement || "");
      setNeighborhood(address.neighborhood || "");
      setCity(address.city || "");
      setState(address.state || "");
      setReference(address.reference || "");
      setIsDefault(address.isDefault || false);
      // Clear objects - they will be matched when data loads
      setNeighborhoodObj(null);
      setCityObj(null);
    } else {
      // Reset form for new address
      setLabel("Casa");
      setZipcode("");
      setStreet("");
      setNumber("");
      setComplement("");
      setNeighborhood("");
      setNeighborhoodObj(null);
      setCity("");
      setCityObj(null);
      setState("");
      setReference("");
      setIsDefault(false);
    }
  }, [address, open]);

  // Match cityObj when cities load and city name is set
  useEffect(() => {
    if (city && cities.length > 0 && !cityObj) {
      const matched = cities.find(c => c.name.toLowerCase() === city.toLowerCase());
      if (matched) {
        setCityObj(matched);
      }
    }
  }, [city, cities, cityObj]);

  // Match neighborhoodObj when neighborhoods load and neighborhood name is set
  useEffect(() => {
    if (neighborhood && neighborhoods.length > 0 && !neighborhoodObj) {
      const matched = neighborhoods.find(n => n.name.toLowerCase() === neighborhood.toLowerCase());
      if (matched) {
        setNeighborhoodObj(matched);
      }
    }
  }, [neighborhood, neighborhoods, neighborhoodObj]);

  const formatCEP = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
  };

  const handleCepChange = (e) => {
    const formatted = formatCEP(e.target.value);
    setZipcode(formatted);

    // Auto-search when CEP is complete
    if (formatted.replace(/\D/g, "").length === 8) {
      handleCepSearch(formatted);
    }
  };

  const handleCepSearch = async (cep) => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) {
      toast.warning("CEP deve ter 8 digitos");
      return;
    }

    setSearchingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error("CEP nao encontrado");
        return;
      }

      setStreet(data.logradouro || "");

      // Set state first (will trigger city loading)
      if (data.uf) {
        setState(data.uf);
      }

      // Set city/neighborhood as text - they will be matched when data loads
      if (data.localidade) {
        setCity(data.localidade);
        setCityObj(null); // Will be matched when cities load
      }
      if (data.bairro) {
        setNeighborhood(data.bairro);
        setNeighborhoodObj(null); // Will be matched when neighborhoods load
      }

      toast.success("Endereco encontrado!");
    } catch (error) {
      console.error("Error searching CEP:", error);
      toast.error("Erro ao buscar CEP");
    } finally {
      setSearchingCep(false);
    }
  };

  const handleStateChange = (e) => {
    const newState = e.target.value;
    setState(newState);
    // Clear city and neighborhood when state changes
    setCity("");
    setCityObj(null);
    setNeighborhood("");
    setNeighborhoodObj(null);
  };

  const handleCityChange = (event, newValue) => {
    if (newValue) {
      setCityObj(newValue);
      setCity(newValue.name);
      // Auto-fill state if city has state info
      if (newValue.state && newValue.state !== state) {
        setState(newValue.state);
      }
    } else {
      setCityObj(null);
      setCity("");
    }
    // Clear neighborhood when city changes
    setNeighborhood("");
    setNeighborhoodObj(null);
  };

  const handleNeighborhoodChange = (event, newValue) => {
    if (newValue) {
      setNeighborhoodObj(newValue);
      setNeighborhood(newValue.name);
    } else {
      setNeighborhoodObj(null);
      setNeighborhood("");
    }
  };

  const handleSubmit = () => {
    // Validation
    const cleanZipcode = zipcode.replace(/\D/g, "");
    if (!cleanZipcode || cleanZipcode.length < 8) {
      toast.error("CEP e obrigatorio (8 digitos)");
      return;
    }
    if (!street.trim()) {
      toast.error("Rua e obrigatoria");
      return;
    }
    if (!number.trim()) {
      toast.error("Numero e obrigatorio");
      return;
    }
    if (!neighborhood.trim()) {
      toast.error("Bairro e obrigatorio");
      return;
    }
    if (!city.trim()) {
      toast.error("Cidade e obrigatoria");
      return;
    }
    if (!state.trim() || state.trim().length < 2) {
      toast.error("Estado e obrigatorio (2 letras)");
      return;
    }

    // Build address data - only include id if editing existing address
    const addressData = {
      label: label.trim() || "Casa",
      zipcode: cleanZipcode,
      street: street.trim(),
      number: number.trim(),
      complement: complement.trim() || undefined,
      neighborhood: neighborhood.trim(),
      neighborhoodId: neighborhoodObj?.id || undefined,
      city: city.trim(),
      cityId: cityObj?.id || undefined,
      state: state.trim().toUpperCase(),
      reference: reference.trim() || undefined,
      isDefault,
    };

    // Include id only when editing
    if (address?.id && address.id !== 'contact-address') {
      addressData.id = address.id;
    }

    onSave(addressData);
  };

  const handleClose = () => {
    onClose();
  };

  const isEditing = !!address?.id;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <LocationIcon color="primary" />
          <Typography variant="h6">
            {isEditing ? "Editar Endereco" : "Novo Endereco"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} className={classes.form}>
          {/* Label */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nome do endereco"
              placeholder="Ex: Casa, Trabalho, Mae..."
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.labelField}
            />
          </Grid>

          {/* Default checkbox */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  color="primary"
                />
              }
              label="Endereco principal"
            />
          </Grid>

          {/* CEP */}
          <Grid item xs={12} sm={4}>
            <Box className={classes.cepField}>
              <TextField
                fullWidth
                label="CEP"
                placeholder="00000-000"
                value={zipcode}
                onChange={handleCepChange}
                variant="outlined"
                size="small"
                inputProps={{ maxLength: 9 }}
              />
              <IconButton
                size="small"
                className={classes.cepSearchButton}
                onClick={() => handleCepSearch(zipcode)}
                disabled={searchingCep}
              >
                {searchingCep ? <CircularProgress size={20} /> : <SearchIcon />}
              </IconButton>
            </Box>
          </Grid>

          {/* Street */}
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Rua *"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>

          {/* Number */}
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="Numero *"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>

          {/* Complement */}
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              label="Complemento"
              placeholder="Apto, Bloco..."
              value={complement}
              onChange={(e) => setComplement(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>

          {/* State */}
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Estado *</InputLabel>
              <Select
                value={state}
                onChange={handleStateChange}
                label="Estado *"
                disabled={loadingStates}
              >
                <MenuItem value="">
                  <em>Selecione...</em>
                </MenuItem>
                {states.map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.code} - {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* City - Autocomplete */}
          <Grid item xs={12} sm={7}>
            <Autocomplete
              className={classes.autocomplete}
              options={cities}
              getOptionLabel={(option) => option.name || ""}
              value={cityObj}
              onChange={handleCityChange}
              loading={loadingCities}
              disabled={!state}
              freeSolo
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setCity(newInputValue);
                }
              }}
              inputValue={city}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cidade *"
                  variant="outlined"
                  size="small"
                  placeholder={state ? "Digite ou selecione..." : "Selecione o estado primeiro"}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={loadingCities ? "Carregando..." : "Nenhuma cidade encontrada"}
            />
          </Grid>

          {/* Neighborhood - Autocomplete */}
          <Grid item xs={12}>
            <Autocomplete
              className={classes.autocomplete}
              options={neighborhoods}
              getOptionLabel={(option) => option.name || ""}
              value={neighborhoodObj}
              onChange={handleNeighborhoodChange}
              loading={loadingNeighborhoods}
              disabled={!cityObj}
              freeSolo
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setNeighborhood(newInputValue);
                }
              }}
              inputValue={neighborhood}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bairro *"
                  variant="outlined"
                  size="small"
                  placeholder={cityObj ? "Digite ou selecione..." : "Selecione a cidade primeiro"}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingNeighborhoods ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={loadingNeighborhoods ? "Carregando..." : "Nenhum bairro encontrado (digite para adicionar)"}
            />
          </Grid>

          {/* Reference */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Ponto de referencia"
              placeholder="Proximo ao mercado..."
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              variant="outlined"
              size="small"
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isEditing ? "Salvar" : "Adicionar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressFormModal;
